import {Form} from "react-bootstrap";
import {useEffect, useRef, useState} from "react";
import "./YesNoSwitch.css";
import { InvalidFeedbackP } from "./InvalidFeedback";

export enum YesNoOptions {
    Yes="Yes",
    No="No",
    NotSelected="NotSelected"
}

export type YesNoProps = {
    controlId: string;
    name: string;
    value: YesNoOptions;
    required: boolean
    label: string|JSX.Element;
    onYesSelected(): void;
    onNoSelected(): void;
    invalidMessage: string,
};
const YesNoSwitch = (props: YesNoProps) => {
    const handleChange = (e: any) => {
        if (e.target.checked) {
            props.onYesSelected();
            return;
        }
        props.onNoSelected();
    }
    return (
        <Form.Group controlId={props.controlId}>
            <Form.Check aria-describedby={`${props.controlId}-invalid`} type="switch" label={props.label} onChange={handleChange}
                        checked={props.value === YesNoOptions.Yes} required={props.required} feedback={props.invalidMessage} feedbackType="invalid" />
           
            
        </Form.Group>
    );
}
YesNoSwitch.defaultProps = {
    required: false,
    invalidMessage: "This is required."
}

export const YesNoToggle = (props: YesNoProps) => {
    const [formSubmitted, setFormSubmitted] = useState(false);
    const yesRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    
    // Check if the form has been submitted
    useEffect(() => {
        const checkFormSubmission = () => {
           const form = document.querySelector('form')?.closest('form');
                if (form) {
                    // Check if the form has the 'was-validated' class, which is added by Bootstrap after validation
                    const wasValidated = form.classList.contains('was-validated');
                    if (wasValidated) {
                        setFormSubmitted(true);
                    }
                }
        };
        
        // Check immediately and also set up an event listener for future form submissions
        checkFormSubmission();
        
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    checkFormSubmission();
                }
            });
        });
        
        // Find the form and observe it for class changes
        if (containerRef.current) {
            const form = containerRef.current.closest('form');
            if (form) {
                observer.observe(form, { attributes: true });
            }
        }
        
        return () => {
            observer.disconnect();
        };
    }, []);
    
    const isChecked = (expected: YesNoOptions):boolean => {
        return props.value as YesNoOptions === expected;
    }

    const yesChecked: boolean = isChecked(YesNoOptions.Yes);
    const noChecked: boolean = isChecked(YesNoOptions.No);
    const yesId: string = `${props.controlId}-yes`;
    const noId: string = `${props.controlId}-no`;
    const required = props.required ? 'required' : '';
    
    // Extract label text from the label (removing any HTML tags if present)
    const getLabelText = () => {
        if (typeof props.label === 'string') {
            // Remove HTML tags from the label
            return props.label.replace(/<[^>]*>/g, '');
        }
        return 'this question';
    }; 
    
    // Generate dynamic error message that includes the question text
    const errorMessage = `Please select Yes or No for "${getLabelText()}"`;
    return (
        <div id={props.controlId} className="yn-container" role="group" aria-labelledby={`${props.controlId}-label`} ref={containerRef}>
            {/* Label */}
            <label 
                id={`${props.controlId}-label`}
                className={`yn-label ${required}`}
                dangerouslySetInnerHTML={{__html: props.label as string}}
            />
            {/* Error message always rendered, CSS controls visibility */}
            <div className="yn-error" id={`${props.controlId}-error-container`}>
                <InvalidFeedbackP id={`${props.controlId}-invalid`}
                    message={errorMessage}
                />
            </div>
            {/* Yes/No buttons */}
            <div className="yn-buttons-container">
                <div className="yn-button-wrapper">                        
                    <input 
                        type="radio"
                        id={yesId}
                        name={props.name}
                        className="yn-radio"
                        checked={yesChecked}
                        onChange={props.onYesSelected}
                        required={props.required}
                        aria-describedby={`${props.controlId}-invalid`}
                        aria-label={`Select Yes for ${typeof props.label === 'string' ? props.label.replace(/<[^>]*>/g, '') : 'this question'}`}
                        ref={yesRef}
                    />
                    <label 
                        htmlFor={yesId}
                        className="yn-radio-label"
                    >
                        Yes
                    </label>
                </div>
                <div className="yn-button-wrapper">
                    <input 
                        type="radio"
                        id={noId}
                        name={props.name}
                        className="yn-radio"
                        checked={noChecked}
                        onChange={props.onNoSelected}
                        required={props.required}
                        aria-describedby={`${props.controlId}-invalid`}
                        aria-label={`Select No for ${typeof props.label === 'string' ? props.label.replace(/<[^>]*>/g, '') : 'this question'}`}
                    />
                    <label 
                        htmlFor={noId}
                        className="yn-radio-label"
                    >
                        No
                    </label>
                </div>
            </div>
        </div>
    );
};
YesNoToggle.defaultProps = {required: false, invalidMessage: ""}

export default YesNoSwitch;